import { z } from "zod";

import { MetaDataSchema } from "@/types/meta-data";

/**
 * Zod schemas + TS types for the onboarding-tour "Lead Scoring → Testing"
 * endpoints (PREC-3700). Mirrors apps/api/src/score/onboarding-test/dto +
 * enums (base path `score/onboarding-test`).
 */

/** Polling state of the "Analyze my changes" background job. */
export enum ScoreTestAnalysisStatus {
	IDLE = "IDLE",
	RUNNING = "RUNNING",
	READY = "READY",
}

/** Lifecycle of an AI refinement suggestion. */
export enum ScoreTestSuggestionStatus {
	PENDING = "PENDING",
	ACCEPTED = "ACCEPTED",
	REJECTED = "REJECTED",
	APPLIED = "APPLIED",
}

/** User decision on a single suggestion (no accept-all by design). */
export enum ScoreTestSuggestionReviewAction {
	ACCEPT = "ACCEPT",
	REJECT = "REJECT",
}

/** Allowed overallScore values on a test result row. */
export const TEST_RESULT_OVERALL_SCORES = ["1", "2", "3", "Disqualified"] as const;

export type TestResultOverallScore = (typeof TEST_RESULT_OVERALL_SCORES)[number];

const LeadScoringCriterionBreakdownSchema = z.object({
	label: z.string(),
	score: z.coerce.number(),
	reasoning: z.string(),
	/**
	 * Set by the scoring engine when this criterion is why the lead is
	 * disqualified. Not inferable from `score`: the API clamps it to >= 0, so a
	 * criterion that matched the rubric's Disqualified bucket and one whose value
	 * merely could not be identified both arrive as 0.
	 */
	disqualifying: z.boolean().optional(),
});

export const LeadScoringBreakdownSchema = z.object({
	company: z.array(LeadScoringCriterionBreakdownSchema),
	persona: z.array(LeadScoringCriterionBreakdownSchema),
	overall: z.object({ score: z.coerce.number(), max: z.coerce.number() }),
});

export type LeadScoringBreakdown = z.infer<typeof LeadScoringBreakdownSchema>;

const TestResultJobTitleToSenioritySchema = z
	.object({
		seniority: z.string().nullable().optional(),
	})
	.loose();

const TestResultOrganizationSchema = z
	.object({
		id: z.coerce.number().optional(),
		name: z.string().nullable().optional(),
		logoUrl: z.string().nullable().optional(),
		linkedinUrl: z.string().nullable().optional(),
		websiteUrl: z.string().nullable().optional(),
		primaryDomain: z.string().nullable().optional(),
		// Firmographic signals the lead scoring rubric evaluates — surfaced as
		// columns in the Testing table so the user can sanity-check the score.
		industry: z.string().nullable().optional(),
		estimatedNumEmployees: z.coerce.number().nullable().optional(),
		employeesFrom: z.coerce.number().nullable().optional(),
		employeesTo: z.coerce.number().nullable().optional(),
		city: z.string().nullable().optional(),
		state: z.string().nullable().optional(),
		country: z.string().nullable().optional(),
	})
	.loose();

const TestResultProspectSchema = z
	.object({
		id: z.coerce.number().optional(),
		name: z.string().nullable().optional(),
		firstName: z.string().nullable().optional(),
		lastName: z.string().nullable().optional(),
		title: z.string().nullable().optional(),
		image: z.string().nullable().optional(),
		linkedinUrl: z.string().nullable().optional(),
		// Persona signals the rubric evaluates — surfaced as Testing-table columns.
		seniority: z.string().nullable().optional(),
		jobTitleToSeniority: TestResultJobTitleToSenioritySchema.nullable().optional(),
		city: z.string().nullable().optional(),
		state: z.string().nullable().optional(),
		country: z.string().nullable().optional(),
		organization: TestResultOrganizationSchema.nullable().optional(),
	})
	.loose();

const TestResultCategorySchema = z
	.object({
		id: z.coerce.number().optional(),
		category: z.string().nullable().optional(),
	})
	.loose();

/** One scored test lead (ScorePersonaCategoryResultInfoDTO). */
export const OnboardingTestResultSchema = z
	.object({
		id: z.coerce.number(),
		prospect: TestResultProspectSchema.nullable().optional(),
		scorePersonaCategory: TestResultCategorySchema.nullable().optional(),
		scoreCompanyCategory: TestResultCategorySchema.nullable().optional(),
		result: z.string().nullable().optional(),
		reason: z.string().nullable().optional(),
		companyScore: z.string().nullable().optional(),
		companyScoringSummary: z.string().nullable().optional(),
		personaScore: z.string().nullable().optional(),
		personaScoringSummary: z.string().nullable().optional(),
		overallScore: z.string().nullable().optional(),
		overallReasoning: z.string().nullable().optional(),
		leadScoringBreakdown: LeadScoringBreakdownSchema.nullable().optional(),
	})
	.loose();

export type OnboardingTestResult = z.infer<typeof OnboardingTestResultSchema>;

/** PageDTO<ScorePersonaCategoryResultInfoDTO>. */
export const OnboardingTestResultsResponseSchema = z.object({
	data: z.array(OnboardingTestResultSchema),
	meta: MetaDataSchema,
});

export type OnboardingTestResultsResponse = z.infer<typeof OnboardingTestResultsResponseSchema>;

/**
 * Whether the Testing sample is ready to show. Building it enriches + AI-scores up to 18 candidates
 * (minutes of work) off the request path, so GET results reports BUILDING with no rows while the
 * job runs — the FE polls until READY (PREC-3719).
 */
export enum OnboardingSampleStatus {
	BUILDING = "BUILDING",
	READY = "READY",
}

/** GET results response (OnboardingSampleResultsDTO): the paged sample plus its build status. */
export const OnboardingSampleResultsResponseSchema = OnboardingTestResultsResponseSchema.extend({
	status: z.enum(OnboardingSampleStatus),
});

export type OnboardingSampleResultsResponse = z.infer<typeof OnboardingSampleResultsResponseSchema>;

/** PATCH results/:id body (UpdateTestResultDto). */
export const UpdateOnboardingTestResultSchema = z.object({
	overallScore: z.enum(TEST_RESULT_OVERALL_SCORES).optional(),
	newCompanyCategoryId: z.number().optional(),
	newPersonaCategoryId: z.number().optional(),
	changeReason: z.string().optional(),
});

export type UpdateOnboardingTestResultRequest = z.infer<typeof UpdateOnboardingTestResultSchema>;

/** PATCH results/:id response (ScoreTestChangeInfoDTO). */
export const OnboardingTestChangeSchema = z
	.object({
		id: z.coerce.number().optional(),
		scorePersonaCategoryResultId: z.coerce.number(),
		previousOverallScore: z.string().nullable().optional(),
		newOverallScore: z.string().nullable().optional(),
		previousCompanyCategoryId: z.coerce.number().nullable().optional(),
		newCompanyCategoryId: z.coerce.number().nullable().optional(),
		previousPersonaCategoryId: z.coerce.number().nullable().optional(),
		newPersonaCategoryId: z.coerce.number().nullable().optional(),
		changeReason: z.string().nullable().optional(),
		analyzedAt: z.string().nullable().optional(),
	})
	.loose();

export type OnboardingTestChange = z.infer<typeof OnboardingTestChangeSchema>;

/** POST analyze response (AnalysisStatusDTO). */
export const AnalysisStatusResponseSchema = z.object({
	status: z.enum(ScoreTestAnalysisStatus),
});

export type AnalysisStatusResponse = z.infer<typeof AnalysisStatusResponseSchema>;

/** One AI refinement suggestion (ScoreTestSuggestionInfoDTO). */
export const OnboardingTestSuggestionSchema = z
	.object({
		id: z.coerce.number(),
		type: z.string().optional(),
		title: z.string(),
		beforeText: z.string().nullable().optional(),
		afterText: z.string().nullable().optional(),
		status: z.enum(ScoreTestSuggestionStatus),
		createdAt: z.string().optional(),
	})
	.loose();

export type OnboardingTestSuggestion = z.infer<typeof OnboardingTestSuggestionSchema>;

/** GET suggestions response (SuggestionsResponseDTO). */
export const OnboardingTestSuggestionsResponseSchema = z.object({
	status: z.enum(ScoreTestAnalysisStatus),
	suggestions: z.array(OnboardingTestSuggestionSchema),
});

export type OnboardingTestSuggestionsResponse = z.infer<
	typeof OnboardingTestSuggestionsResponseSchema
>;

/** PATCH suggestions/:id body (ReviewSuggestionDto). */
export const ReviewOnboardingTestSuggestionSchema = z.object({
	action: z.enum(ScoreTestSuggestionReviewAction),
});

/** GET/POST ceo-split response (CeoSplitInfoDTO). */
export const CeoSplitInfoSchema = z.object({
	exists: z.boolean(),
	employeeCutoff: z.coerce.number().optional().nullable(),
});

export type CeoSplitInfo = z.infer<typeof CeoSplitInfoSchema>;

/** POST ceo-split body (CreateCeoSplitDto). */
export const CreateCeoSplitSchema = z.object({
	employeeCutoff: z.number().int().min(1),
});

export type CreateCeoSplitRequest = z.infer<typeof CreateCeoSplitSchema>;
